import React, { useState, useEffect, useMemo } from 'react';
import { Input, Button, Avatar, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { fetchRooms } from '../../state/actions/room';
import { setActiveTab } from '../../state/actions/tab';
import VirtualCalendar from '../../components/VirtualCalendar';
import VirtualCalendarWeekly from '../../components/VirtualCalendar/VirtualCalendarWeekly';
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import './Dashboard.css';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { siteId } = useParams();
  const [search, setSearch] = useState('');
  const [searchError, setSearchError] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(new Set());
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [roomSearchTerm, setRoomSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('daily');
  const [currentDate, setCurrentDate] = useState(new Date());

  const { rooms, loading, activeTab } = useSelector((state) => ({
    rooms: state.room?.rooms || [],
    loading: state.room?.loading || false,
    activeTab: state.tab?.tab,
  }));


  // Set active tab when component mounts
  useEffect(() => {
    dispatch(setActiveTab({ tab: 'dashboard' }));
  }, [dispatch]);

  const selectedRoomsArray = useMemo(
    () => (selectedRoom.size === 0 ? [] : [...selectedRoom]),
    [selectedRoom]
  );
  // Fetch rooms when component mounts or tab becomes active
  useEffect(() => {
    if (siteId && activeTab === 'dashboard') {
      const roomIdFilter = viewMode === 'daily' && selectedRoomsArray.length > 0  ? selectedRoomsArray : undefined;
      dispatch(fetchRooms({
        siteId,
        roomId: roomIdFilter
      }));
    }
  }, [dispatch, siteId, activeTab, selectedRoom, viewMode]);

  const handleSearch = (e) => {
    setSearchError(null);
    const value = e.target.value;
    setSearch(value);
  };

  const handleRoomSelect = (roomId) => {
    if(roomId === 'all'){
      setSelectedRoom(new Set());
    }else{

    setSelectedRoom((prev) => {
      const updated = new Set(prev); // copy old state

      if (updated.has(roomId)) {
        updated.delete(roomId); // remove if already selected
      } else {
        updated.add(roomId); // add if not selected
      }

      return updated;
    });
    }
    // setPlaylistOpen(false);
  };

  const handleSubmit = () => {
  };

  const formatDate = (date) => {
    const options = {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
  };

  const handlePrevDate = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'daily') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() - 7);
    }
    setCurrentDate(newDate);
  };

  const handleNextDate = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'daily') {
      newDate.setDate(newDate.getDate() + 1);
    } else {
      newDate.setDate(newDate.getDate() + 7);
    }
    setCurrentDate(newDate);
  };

  const handleDateChange = (date) => {
    if (date) {
      setCurrentDate(date.toDate());
    }
  };

  // Filter rooms based on search term
  const filteredRooms = rooms.filter(room =>
    room.name.toLowerCase().includes(roomSearchTerm.toLowerCase()) ||
    room._id.toLowerCase().includes(roomSearchTerm.toLowerCase())
  );

  // Calculate date range for calendar based on view mode and current date
  const getDateRange = () => {
    if (viewMode === 'daily') {
      const start = moment(currentDate).startOf('day');
      const end = moment(currentDate).endOf('day');
      return {
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD')
      };
    } else {
      // For weekly view, ensure we get full Sunday to Saturday week
      const start = moment(currentDate).startOf('week'); // Sunday
      const end = moment(currentDate).endOf('week'); // Saturday
      return {
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD')
      };
    }
  };



  return (
    <div className="dashboard">
      <div className="screens__nav">
        <div className="screens__search-wrapper">
          {/* Left side - Search and Room Dropdown */}
          <div className="left-side-controls">
            {/* Main search */}
            <div className="media-categories__search">
              <i className="feather-search" />
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={handleSearch}
              />
            </div>
            {searchError && <div className="search-error">{searchError}</div>}

            {/* Room Dropdown */}
            <div className="playlist-wrapper" onClick={() => setPlaylistOpen(!playlistOpen)}>
              <div className="playlist-icon">
                <i className="feather-layers"></i>
              </div>
              <span className="playlist-text">
                {selectedRoom.size === 0 ? 'All Rooms' : ([...selectedRoom]
                  .map(id => rooms.find(r => r._id === id)?.name)
                  .filter(Boolean) // remove undefined if some id not found
                  .join(", ")|| 'All Rooms')}
              </span>
              <div className={`playlist-chevron ${playlistOpen ? 'open' : ''}`}>
                <i className="feather-chevron-down"></i>
              </div>
              {playlistOpen && (
                <div className="playlist-dropdown">
                  {/* Search within dropdown */}
                  <div className="room-search-wrapper">
                    <Input
                      placeholder="Search"
                      prefix={<SearchOutlined />}
                      value={roomSearchTerm}
                      onChange={(e) => setRoomSearchTerm(e.target.value)}
                      className="room-search-input"
                    />
                  </div>

                  {/* All Rooms option */}
                  <div
                    className={`room-option ${selectedRoom.size === 0 ? 'selected' : ''}`}
                    onClick={() => handleRoomSelect('all')}
                  >
                    <Avatar size={48} className="room-avatar">
                      <i className="feather-layers" />
                    </Avatar>
                    <div className="room-info">
                      <div className="room-name">All Rooms</div>
                      <div className="room-id">View all rooms</div>
                    </div>
                  </div>

                  {/* Room options */}
                  {filteredRooms.map(room => (
                    <div
                      key={room._id}
                      className={`room-option ${selectedRoom.has(room._id)  ? 'selected' : ''}`}
                      onClick={() => handleRoomSelect(room._id)}
                    >
                      <Avatar
                        size={48}
                        src={room.image}
                        className="room-avatar"
                      >
                        {!room.image && <i className="feather-image" />}
                      </Avatar>
                      <div className="room-info">
                        <div className="room-name">{room.name}</div>
                        <div className="room-id">ID: {room._id.slice(-8)}</div>
                      </div>
                    </div>
                  ))}

                  {/* Submit button */}
                  <div className="room-dropdown-footer">
                    <Button
                      type="primary"
                      block
                      size="large"
                      onClick={handleSubmit}
                      className="room-submit-btn"
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right side controls */}
          <div className="right-side-controls">
            {/* Daily/Weekly Toggle */}
            <div className="view-mode-toggle">
              <button
                className={`toggle-btn ${viewMode === 'daily' ? 'active' : ''}`}
                onClick={() => setViewMode('daily')}
              >
                Daily
              </button>
              <button
                className={`toggle-btn ${viewMode === 'weekly' ? 'active' : ''}`}
                onClick={() => setViewMode('weekly')}
              >
                Weekly
              </button>
            </div>

            {/* Date Navigation */}
            <div className="date-navigation">
              <button className="nav-btn" onClick={handlePrevDate}>
                <i className="feather-chevron-left"></i>
              </button>

              <div className="date-display">
                <DatePicker
                  value={dayjs(currentDate)}
                  onChange={handleDateChange}
                  format="YYYY-MM-DD"
                  placeholder="Select date"
                  style={{ marginRight: '8px' }}
                />
                {dayjs(currentDate).isSame(dayjs(), 'day') && (
                  <div className="today-btn">
                    Today
                  </div>
                )}
              </div>

              <button className="nav-btn" onClick={handleNextDate}>
                <i className="feather-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="screens__button-group">
          {/* Add dashboard specific buttons here */}
        </div>
      </div>



      <div className="dashboard-content">
        {viewMode === 'weekly' ? (
          <VirtualCalendarWeekly
            dateRange={getDateRange()}
            search={search}
            selectedRoom={selectedRoom}
            selectedRooms={selectedRoomsArray}
            viewMode={viewMode}
            siteId={siteId}
            rooms={rooms}
          />
        ) : (
          <VirtualCalendar
            selectedRooms={selectedRoomsArray}
            dateRange={getDateRange()}
            search={search}
            viewMode={viewMode}
            siteId={siteId}
            rooms={rooms}
          />
        )}
      </div>
    </div>
  );
};

export default Dashboard;
